<template>
    <Modal2
        v-if="visible"
        :title="$t('message.capiEventModalTitle')"
        @cancel="handleCancel"
        @ok="handleSend"
        :okText="$t('message.send')"
        :cancelText="$t('message.cancel')"
    >
        <div class="send-capi-event-form">
            <!-- Kanal Seçimi -->
            <div class="form-group">
                <label>{{ $t("message.capiChannelLabel") }} <span class="required">*</span></label>
                <a-select
                    v-model="form.channelId"
                    :placeholder="$t('message.capiSelectChannelPlaceholder')"
                    style="width: 100%"
                    @change="handleChannelChange"
                    readonly
                    disabled
                >
                    <a-select-option v-for="channel in availableChannels" :key="channel._id" :value="channel._id">
                        {{ channel.name }} ({{ channel.type}})
                    </a-select-option>
                </a-select>
            </div>

            <!-- Event Type -->
            <div class="form-group">
                <label>{{ $t("message.capiEventTypeLabel") }} <span class="required">*</span></label>
                <a-select v-model="form.eventType" :placeholder="$t('message.capiEventTypePlaceholder')" style="width: 100%">
                    <a-select-option value="LeadSubmitted">{{ $t("message.capiEventLeadSubmitted") }}</a-select-option>
                    <a-select-option value="Purchase">{{ $t("message.capiEventPurchase") }}</a-select-option>
                    <a-select-option value="InitiateCheckout">{{ $t("message.capiEventInitiateCheckout") }}</a-select-option>
                    <a-select-option value="AppointmentBooked">{{ $t("message.capiEventAppointmentBooked") }}</a-select-option>
                    <a-select-option value="AddToCart">{{ $t("message.capiEventAddToCart") }}</a-select-option>
                    <a-select-option value="ViewContent">{{ $t("message.capiEventViewContent") }}</a-select-option>
                    <a-select-option value="OrderCreated">{{ $t("message.capiEventOrderCreated") }}</a-select-option>
                    <a-select-option value="OrderShipped">{{ $t("message.capiEventOrderShipped") }}</a-select-option>
                </a-select>
            </div>

            <!-- Currency (purchase-related events) -->
            <div
                class="form-group"
                v-if="['Purchase', 'OrderCreated', 'OrderShipped'].includes(form.eventType)"
            >
                <label>{{ $t("message.capiCurrencyLabel") }}</label>
                <a-select v-model="form.currency" :placeholder="$t('message.capiCurrencyPlaceholder')" style="width: 100%">
                    <a-select-option value="TRY">{{ $t("message.currencyOptionTRY") }}</a-select-option>
                    <a-select-option value="USD">{{ $t("message.currencyOptionUSD") }}</a-select-option>
                    <a-select-option value="EUR">{{ $t("message.currencyOptionEUR") }}</a-select-option>
                    <a-select-option value="GBP">{{ $t("message.currencyOptionGBP") }}</a-select-option>
                </a-select>
            </div>

            <!-- Value (purchase-related events) -->
            <div
                class="form-group"
                v-if="['Purchase', 'OrderCreated', 'OrderShipped'].includes(form.eventType)"
            >
                <label>{{ $t("message.capiAmountLabel") }}</label>
                <a-input-number
                    v-model="form.value"
                    :placeholder="$t('message.capiAmountPlaceholder')"
                    :min="0"
                    :step="0.01"
                    style="width: 100%"
                />
            </div>

            <!-- Custom Data -->
            <div class="form-group">
                <label>{{ $t("message.capiCustomDataLabel") }}</label>
                <div class="custom-data-list">
                    <div v-for="(item, index) in form.customDataList" :key="index" class="custom-data-item">
                        <a-input v-model="item.key" :placeholder="$t('message.capiCustomDataKeyPlaceholder')" style="width: 40%" />
                        <a-input v-model="item.value" :placeholder="$t('message.capiCustomDataValuePlaceholder')" style="width: 50%" />
                        <SupsisButton danger icon="delete" @click="removeCustomData(index)" />
                    </div>
                    <SupsisButton @click="addCustomData" icon="add">
                        {{ $t("message.capiAddFieldButton") }}
                    </SupsisButton>
                </div>
            </div>

            <!-- Contact info -->
            <div class="info-section" v-if="contact">
                <h4>{{ $t("message.capiContactInfoHeading") }}</h4>
                <div class="info-grid">
                    <div class="info-item">
                        <span class="label">{{ $t("message.capiContactNameLabel") }}</span>
                        <span class="value">{{ contact.fullname || $t("message.na") }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">{{ $t("message.capiContactPlatformLabel") }}</span>
                        <span class="value">{{ contact.platform }}</span>
                    </div>
                    <div class="info-item" v-if="contact.meta_ids">
                        <span class="label">{{ $t("message.capiContactMetaIdLabel") }}</span>
                        <span class="value">{{ getMetaIdDisplay() }}</span>
                    </div>
                </div>
            </div>
        </div>
    </Modal2>
</template>

<script>
import { mapState } from "vuex";
import _ from "lodash";
import { CAPI_API } from "../../api/capi";
import Modal2 from "../common/Modal2.vue";
import SupsisButton from "../../views/Pages/Settings/components/SupsisButton.vue";

export default {
    name: "SendCapiEventModal",
    components: {
        Modal2,
        SupsisButton,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        contact: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            form: {
                channelId: null,
                eventType: "Purchase",
                currency: "TRY",
                value: 0,
                customDataList: [],
            },
            sending: false,
        };
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels,
        }),
        availableChannels() {
            // Contact'ın kanalını bul veya tüm Instagram/Messenger kanalları göster
            const metaChannels = this.channels.filter(
                (c) =>
                    ["instagram", "instagram-v2", "messenger","meta-leads"].includes(c.type) &&
                    _.get(c, "capi.enabled") === true &&
                    _.get(c, "capi.datasetId")
            );

            // Eğer contact'ın kanalı varsa onu varsayılan yap
            if (this.contact && this.contact.channel) {
                const contactChannel = metaChannels.find((c) => c._id === this.contact.channel);
                if (contactChannel && !this.form.channelId) {
                    this.form.channelId = contactChannel._id;
                }
            }

            return metaChannels;
        },
    },
    methods: {
        handleChannelChange() {
            // Kanal değiştiğinde gerekirse form'u sıfırla
        },
        addCustomData() {
            this.form.customDataList.push({
                key: "",
                value: "",
            });
        },
        removeCustomData(index) {
            this.form.customDataList.splice(index, 1);
        },
        getMetaIdDisplay() {
            if (!this.contact || !this.contact.meta_ids) return "N/A";

            const metaIds = this.contact.meta_ids;
            if (metaIds.ig_sid) return metaIds.ig_sid;
            if (metaIds.page_scoped_user_id) return metaIds.page_scoped_user_id;
            return "N/A";
        },
        async handleSend() {
            // Validasyon
            if (!this.form.channelId) {
                this.$message.error("Lütfen bir kanal seçin");
                return;
            }

            if (!this.form.eventType) {
                this.$message.error("Lütfen event tipi seçin");
                return;
            }

            if (!this.contact || !this.contact._id) {
                this.$message.error("Kişi bilgisi bulunamadı");
                return;
            }

            this.sending = true;

            try {
                // Custom data'yı oluştur
                const customData = {};

                if (["Purchase", "OrderCreated", "OrderShipped"].includes(this.form.eventType)) {
                    customData.currency = this.form.currency;
                    customData.value = this.form.value;
                }

                // Ek alanları ekle
                this.form.customDataList.forEach((item) => {
                    if (item.key && item.value) {
                        customData[item.key] = item.value;
                    }
                });

                // Event gönder
                await CAPI_API.sendEvent(this.form.channelId, this.contact._id, this.form.eventType, customData);

                this.$message.success("CAPI event başarıyla gönderildi");
                this.handleCancel();
            } catch (error) {
                console.error("CAPI event gönderme hatası:", error);
                this.$message.error("Event gönderilemedi: " + (error.response?.data || error.message));
            } finally {
                this.sending = false;
            }
        },
        handleCancel() {
            this.$emit("cancel");
            this.resetForm();
        },
        resetForm() {
            this.form = {
                channelId: null,
                eventType: "Purchase",
                currency: "TRY",
                value: 0,
                customDataList: [],
            };
        },
    },
    created() {
        console.log("SendCapiEventModal created", this.contact);
        if (this.contact) {
            this.form.channelId = this.contact.channel;
        }
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                // Modal açıldığında formu hazırla
                if (this.contact && this.contact.channel) {
                    const contactChannel = this.availableChannels.find((c) => c._id === this.contact.channel);
                    if (contactChannel) {
                        this.form.channelId = contactChannel._id;
                    }
                }
            }
        },
        contact: {
            deep: true,
            handler(newVal) {
                if (newVal) {
                    this.form.channelId = newVal.channel;
                }
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.send-capi-event-form {
    display: flex;
    flex-direction: column;
    gap: 20px;

    .form-group {
        display: flex;
        flex-direction: column;
        gap: 8px;

        label {
            font-weight: 500;
            color: var(--color-text-default);

            .required {
                color: #ff4d4f;
            }
        }

        .custom-data-list {
            display: flex;
            flex-direction: column;
            gap: 10px;

            .custom-data-item {
                display: flex;
                gap: 10px;
                align-items: center;
            }
        }
    }

    .info-section {
        padding: 15px;
        background: var(--color-bg-subtle);
        border-radius: 6px;
        border: 1px solid var(--color-border-default);

        h4 {
            margin: 0 0 12px 0;
            font-size: 14px;
            font-weight: 600;
            color: var(--color-text-default);
        }

        .info-grid {
            display: flex;
            flex-direction: column;
            gap: 8px;

            .info-item {
                display: flex;
                justify-content: space-between;
                font-size: 13px;

                .label {
                    font-weight: 500;
                    color: var(--color-text-subtle);
                }

                .value {
                    color: var(--color-text-default);
                }
            }
        }
    }
}
</style>



